import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CONSTANTS, DOC_TEMPLATES } from '../../constants'
import TextareaAutosize from 'react-textarea-autosize'
import { selectDocumentState, setContent, setDrafts, setTitle } from '../../redux/docSlice'
import './_Create.scss'
import { DocumentState } from '../../redux/types'
import DocumentWrapper from '../../Components/DocumentWrapper'
import Setup from './Setup'
import ErrorModal from '../../Components/Modals/Error'
import { useAtom } from 'jotai'
import { accountModalAtom } from './Create'
import { selectGraderModeOn, selectUser } from '../../redux/systemSlice'
import CategorySelector from '../../Components/Grader/CategorySelector'
import { Skeleton } from '@mui/material'
import useLoadingDots from '../../hooks/loadingDots'
import { LuArrowRight, LuBookText, LuPencilLine, LuSearch, LuText, LuTextSelect, LuX } from 'react-icons/lu'
import Button from '../../Components/Buttons'
import { useHistory } from 'react-router-dom'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'
import GeneratingEssay from './Generating'

type CreateProps = {
	type: keyof typeof DOC_TEMPLATES | undefined
	docID: string
}

const freeUserLoadingSteps = ['Creating outline', 'Writing essay', 'Formatting']
const newFreeUserLoadingSteps = [
	{ label: 'Creating outline', description: 'Mapping the thesis and section plan.', icon: LuText },
	{ label: 'Writing essay', description: 'Drafting clear, connected paragraphs.', icon: LuPencilLine },
	{ label: 'Formatting', description: 'Polishing citations and layout to your style guide.', icon: LuTextSelect },
]

function Essay(props: CreateProps) {
	const dispatch = useDispatch()
	const outlinerUpsellFF = useFeatureFlagVariantKey(FEATURE_FLAGS.OUTLINER_UPSELL)
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, props.docID))
	const [topicInputFocused, setTopicInputFocused] = useState<boolean>(false)
	const [loadingStatusIndex, setLoadingStatusIndex] = useState<number>(0)
	const [errorModalOpen, setErrorModalOpen] = useState(false)
	const [showOutlineUpgrade, setShowOutlineUpgrade] = useState(true)
	const [, setAccountModalState] = useAtom(accountModalAtom)
	const documentRef = useRef<HTMLDivElement>(null)
	const headerRef = useRef<HTMLDivElement>(null)
	const graderModeOn = useSelector(selectGraderModeOn)
	const user = useSelector(selectUser)
	const topicInputRef = useRef(null)
	const history = useHistory()
	const loadingDots = useLoadingDots(documentState && (documentState.isHumanizing || documentState.isGenerating))
	const updatedLoaderFF = useFeatureFlagVariantKey(FEATURE_FLAGS.UPDATED_LOADER)

	const showDrafts = documentState && documentState.setup && (documentState.drafts?.length ?? 0) > 0

	const updatedLoadingSteps = useMemo(() => {
		if (user.planId) {
			return [
				{
					label: 'Finding sources',
					description: 'Gathering credible references and key quotes.',
					icon: LuBookText,
				},
				{
					label: 'Analyzing topic',
					description: 'Clarifying the prompt and main arguments.',
					icon: LuSearch,
				},
				{
					label: 'Creating outline',
					description: 'Mapping the thesis and section plan.',
					icon: LuText,
				},
				{
					label: 'Writing essay',
					description: 'Drafting clear, connected paragraphs.',
					icon: LuPencilLine,
				},
				{
					label: 'Formatting',
					description: 'Polishing citations and layout to your style guide.',
					icon: LuTextSelect,
				},
			]
		} else if (documentState && documentState?.userWriterSelections?.refStyle === null) {
			return newFreeUserLoadingSteps
		} else {
			return [
				{ label: 'Finding sources', description: 'Gathering credible references and key quotes.', icon: LuBookText },
				...newFreeUserLoadingSteps,
			]
		}
	}, [documentState?.userWriterSelections?.refStyle, documentState?.isGenerating, user.planId])

	const loadingSteps = useMemo(() => {
		if (user.planId) {
			return ['Finding sources', 'Creating outline', 'Writing body', 'Writing conclusion', 'Formatting']
		} else if (documentState && documentState?.userWriterSelections?.refStyle === null) {
			return freeUserLoadingSteps
		} else {
			return ['Finding sources', ...freeUserLoadingSteps]
		}
	}, [documentState?.userWriterSelections?.refStyle, documentState?.isGenerating, user.planId])

	const intervalLength = useMemo(() => {
		if (!user.planId) {
			return 1500
		}
		if (documentState?.userWriterSelections?.wordCount < 1600) {
			return 3000
		}
		return 5000
	}, [documentState?.userWriterSelections?.refStyle, documentState?.userWriterSelections?.wordCount, user.planId])

	useEffect(() => {
		if (documentState && !documentState.isGenerating) {
			setLoadingStatusIndex(0)
		}
	}, [documentState?.isGenerating])

	useEffect(() => {
		let intervalId: any

		if (documentState?.isGenerating) {
			intervalId = setInterval(() => {
				setLoadingStatusIndex((prevIndex) => {
					if (prevIndex < loadingSteps.length - 1) {
						return prevIndex + 1
					} else {
						return prevIndex // Stay at the last index
					}
				})
			}, intervalLength)
		} else {
			setLoadingStatusIndex(0) // Reset when not generating
		}

		return () => clearInterval(intervalId) // Cleanup on unmount or loading changes
	}, [documentState?.isGenerating, loadingSteps, intervalLength])

	if (!documentState?.setup) {
		return (
			<>
				<ErrorModal open={errorModalOpen} closeModal={() => setErrorModalOpen(false)} />
				<div id="create" className={`pt-16 pb-32 px-4 lg:px-0 md:max-w-4xl md:m-auto`}>
					{documentState && (
						<Setup
							type={props.type}
							docID={props.docID}
							setSubscriptionModalOpen={(open) =>
								setAccountModalState((prev) => ({ ...prev, subscriptionModalOpen: open }))
							}
						/>
					)}
					<div ref={documentRef}>
						{documentState && documentState.isHumanizing && !documentState.isGenerating && (
							<div className="w-80 absolute bg-white shadow z-10 top-80 left-[calc(50%-130px)] py-2 px-5 border border-gray-300 rounded">
								<h6 className="text-2xl fancy-font text-center">Humanizing{loadingDots}</h6>
								<div className="text-gray-600 text-center">This may take up to a minute.</div>
							</div>
						)}
						{documentState && documentState.isGenerating && (
							<>
								<div className="mt-10">
									{[...Array(10)].map((_, index) => (
										<Skeleton key={index} variant="text" width="100%" height={40} animation="wave" />
									))}
								</div>
								<div
									className={`w-72 absolute bg-white shadow z-10 top-80 left-[calc(50%-145px)] py-2 px-5 border border-gray-300 rounded`}
								>
									<h6 className="text-2xl text-center">
										{props.type === DOC_TEMPLATES.Outline ? 'Writing outline' : loadingSteps[loadingStatusIndex]}
										{loadingDots}
									</h6>
									<div className="text-gray-600 text-center">This may take up to a minute.</div>
								</div>
							</>
						)}
						<div className={documentState && documentState.isHumanizing ? 'humanizing' : ''}>
							<DocumentWrapper docID={props.docID} type={props.type} />
						</div>
					</div>

					{/* {documentState?.content && !documentState.isGenerating && documentState.isTypingEssay && (
					<Basic
						onClick={() => {
							if (reader) reader.cancel()
						}}
						type="tertiary"
						CTA={<span>Stop generating</span>}
						className="abortButton"
					/>
				)} */}
				</div>
			</>
		)
	}

	return (
		<>
			<div className="bg-gray-50 md:pt-16 pt-12 min-h-screen pb-36">
				<ErrorModal open={errorModalOpen} closeModal={() => setErrorModalOpen(false)} />

				{showDrafts && (
					<div className="sticky top-10 md:top-[54px] bg-gray-50 md:pb-1 z-10 md:pt-4 pt-1 pl-2">
						<div
							className={`flex gap-3 justify-start md:mb-4 mb-1 pb-4 pt-3 md:pt-0 md:pb-0 items-center md:max-w-[1200px] md:m-auto`}
						>
							<div
								className={`ml-0 ${
									documentState.drafts?.[0]?.selected || documentState.isGenerating
										? 'bg-primary text-white'
										: 'bg-white hover:bg-gray-100 border border-gray-300'
								} rounded-lg px-4 py-1 font-semibold cursor-pointer`}
								onClick={() => {
									dispatch(setContent({ docID: props.docID, content: documentState.drafts[0].content }))
									dispatch(
										setDrafts({
											docID: props.docID,
											drafts: [
												{ content: documentState.drafts[0].content, selected: true },
												{ content: documentState.drafts[1].content, selected: false },
											],
										})
									)
								}}
							>
								Draft #1
							</div>
							{(documentState?.drafts?.length ?? 0) !== 2 || documentState?.isHumanizing ? (
								<Skeleton
									variant="rectangular"
									height={32}
									width={100}
									style={{ borderRadius: '10px', textAlign: 'center', paddingTop: '3px', fontWeight: '600' }}
								>
									Draft #2
								</Skeleton>
							) : (
								<div
									className={`${
										(documentState?.drafts?.length ?? 0) === 2 && documentState.drafts[1].selected
											? 'bg-primary text-white'
											: 'bg-white hover:bg-gray-100 border border-gray-300'
									} rounded-lg px-4 py-1 font-semibold cursor-pointer`}
									onClick={() => {
										dispatch(setContent({ docID: props.docID, content: documentState.drafts[1].content }))
										dispatch(
											setDrafts({
												docID: props.docID,
												drafts: [
													{ content: documentState.drafts[0].content, selected: false },
													{ content: documentState.drafts[1].content, selected: true },
												],
											})
										)
									}}
								>
									Draft #2
								</div>
							)}
						</div>
					</div>
				)}
				{outlinerUpsellFF === 'test' && props.type === DOC_TEMPLATES.Outline && showOutlineUpgrade && (
					<div className=" md:max-w-[1200px] md:mx-auto flex items-center justify-center flex-col gap-2 py-6 px-3 w-full my-8 overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-[#eff6ff] to-[#eef2ff] shadow-lg relative">
						<Button type="ghost" className="absolute top-2 right-2" onClick={() => setShowOutlineUpgrade(false)}>
							{LuX({ className: 'w-5 h-5' })}
						</Button>
						<div className="text-2xl font-semibold text-center">Love your outline?</div>
						<div className="text-lg text-gray-700 max-w-[650px] text-center font-medium">
							Transform it into a complete, well-written essay with our Al Essay Writer.
						</div>
						<Button
							type="default"
							className={`border border-primary px-9 py-2 bg-primary text-white font-semibold border-dotted mt-2 `}
							onClick={() => history.push('/new/' + DOC_TEMPLATES.Writer)}
						>
							<div className="flex items-center gap-2">
								<div>Generate full essay</div>
								{LuArrowRight({ className: 'w-5 h-5' })}
							</div>
						</Button>
					</div>
				)}
				<div className={`flex gap-2 md:mx-auto md:max-w-[1200px]`}>
					<div className={`flex flex-col md:flex-row item gap-2 md:gap-0 grow`}>
						<div
							id="create"
							className={`py-4 md:px-12 px-2 mt-0 md:mt-3 bg-white rounded-lg shadow animate-fade-in-up w-full`}
						>
							{graderModeOn && documentState && documentState.setup && <CategorySelector />}
							{documentState && (
								<Setup
									type={props.type}
									docID={props.docID}
									setSubscriptionModalOpen={(open) =>
										setAccountModalState((prev) => ({ ...prev, subscriptionModalOpen: open }))
									}
								/>
							)}
							{documentState && documentState.setup && (
								<>
									<div className={`headerContainer `} ref={headerRef}>
										<TextareaAutosize
											placeholder="Untitled"
											id="topicHeader"
											className={`pb-2 w-full resize-none outline-none font-semibold ${
												(documentState?.title?.length ?? 0) > 100 ? 'md:text-2xl text-2xl' : 'md:text-2xl text-3xl'
											}`}
											ref={topicInputRef}
											onChange={(e) => {
												dispatch(setTitle({ docID: props.docID, title: e.target.value }))
											}}
											onFocus={() => {
												setTopicInputFocused(true)
											}}
											onBlur={() => {
												setTopicInputFocused(false)
											}}
											minRows={1}
											maxRows={topicInputFocused ? undefined : 3}
											maxLength={CONSTANTS.MAX_TOPIC_LENGTH}
											value={documentState.title}
										/>
										{documentState.isGenerating ||
										documentState.isHumanizing ||
										documentState.isTypingEssay ||
										documentState.isTypingAddMore ? (
											<div className="oscillating-bar"></div>
										) : (
											<div className="topicDivider border-t border-gray-300"></div>
										)}
									</div>
								</>
							)}
							<div ref={documentRef} className={updatedLoaderFF === 'test' ? '' : 'bg-gray-50'}>
								{documentState && documentState.isHumanizing && !documentState.isGenerating && (
									<div className="w-80 absolute bg-white shadow z-10 top-80 left-[calc(50%-130px)] py-2 px-5 border border-gray-300 rounded">
										<h6 className="text-2xl text-center">Humanizing{loadingDots}</h6>
										<div className="text-gray-600 text-center">This may take up to a minute.</div>
									</div>
								)}
								{documentState && documentState.isGenerating && (
									<>
										{updatedLoaderFF === 'test' ? (
											<GeneratingEssay
												loadingSteps={updatedLoadingSteps}
												loadingStatusIndex={loadingStatusIndex}
												loadingDots={loadingDots}
											/>
										) : (
											<>
												<div className="mt-10 bg-white">
													{[...Array(10)].map((_, index) => (
														<Skeleton key={index} variant="text" width="100%" height={40} animation="wave" />
													))}
												</div>
												<div
													className={`w-72 absolute bg-white shadow z-10 top-64 left-[calc(50%-145px)] py-2 px-5 border border-gray-300 rounded`}
												>
													<h6 className="text-2xl text-center">
														{props.type === DOC_TEMPLATES.Outline
															? 'Writing outline'
															: loadingSteps[loadingStatusIndex]}
														{loadingDots}
													</h6>
													<div className="text-gray-600 text-center">This may take up to a minute.</div>
												</div>
											</>
										)}
									</>
								)}
								<div className={documentState && documentState.isHumanizing ? 'humanizing' : ''}>
									<DocumentWrapper docID={props.docID} type={props.type} />
								</div>
							</div>

							{/* {documentState?.content && !documentState.isGenerating && documentState.isTypingEssay && (
				<Basic
					onClick={() => {
						if (reader) reader.cancel()
					}}
					type="tertiary"
					CTA={<span>Stop generating</span>}
					className="abortButton"
				/>
			)} */}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Essay
